import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CurveDeskPanel from '../components/Curve/CurveDeskPanel';
import CurveRail from '../components/Curve/CurveRail';
import {
  fetchToday,
  fetchTape,
  getGuestToken,
  clearGuestToken,
  claimGuestDraws,
} from '../components/Curve/curveApi';
import { useShell } from './ShellContext';
import { useShellUI } from './AppShell';
import { leagueMeta } from './leagueUtils';
import {
  orphanMarketplaceAPI,
  startupMarketplaceAPI,
  tradePackagesAPI,
  managerIntelAPI,
  sleeperAPI,
} from '../utils/api';
import { ChevronRightIcon } from './icons';
import { SleeperAvatar } from './SleeperAvatar';
import LinkSleeperBlock from './LinkSleeperBlock';

/**
 * Home — the shell's landing surface, rebuilt around the daily game.
 * Both auth states share the desk: today's Curve playable in place,
 * Manager Intel search, Analyst Review. Logged in adds leagues, resume
 * cards, and attention rows below; the rail carries the Curve tape and
 * leaguemate scouting.
 */

const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const TRADE_TOOLS = [
  {
    path: '/simulate',
    label: 'Simulate',
    desc: 'Run your season 100,000 times and see how potential trades impact your results using real betting odds and player projections.',
  },
  {
    path: '/trade-packages',
    label: 'Trade Builder',
    desc: 'Three offers in one link. Deals close faster when they choose.',
  },
  {
    path: '/rate-my-trade',
    label: 'Rate My Trade',
    desc: 'Post it, let the internet vote, pull the verdict.',
  },
];

/* Logged-out rail — live marketplace + recently scouted (real data). */
const HomeRail = ({ recentManagers, navigate }) => (
  <>
    <div className="ft-railsec">
      <span className="ft-k">Simulate your league</span>
      <p style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', margin: '6px 0 10px', lineHeight: 1.45 }}>
        Real projections and odds — see your season played out 100,000 times.
      </p>
      <button className="ft-railaction" onClick={() => navigate('/simulate')}>
        <span>Run your league</span>
        <ChevronRightIcon />
      </button>
    </div>
    {recentManagers.length > 0 && (
      <div className="ft-railsec">
        <span className="ft-k">Recently scouted</span>
        {recentManagers.map((m) => (
          <button
            key={m.username}
            className="ft-railaction"
            onClick={() => navigate(`/manager-intel/${m.username}`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <SleeperAvatar url={m.avatarUrl} name={m.username} size={24} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.username}</span>
            </span>
            <ChevronRightIcon />
          </button>
        ))}
      </div>
    )}
  </>
);

/* Logged-in rail — the Curve tape (same component as /curve) + leaguemates. */
const AuthedRail = ({ user, tape, todayLineId, selfUsername, selfAvatar, mates, navigate }) => (
  <>
    <CurveRail user={user} tape={tape} todayLineId={todayLineId} onPick={() => navigate('/curve')} />
    {(selfUsername || mates.length > 0) && (
      <div className="ft-railsec" style={{ marginTop: 26 }}>
        <span className="ft-k">Scout your leaguemates</span>
        {selfUsername && (
          <button
            className="ft-railaction"
            onClick={() => navigate(`/manager-intel/${encodeURIComponent(selfUsername)}`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <SleeperAvatar id={selfAvatar} name={selfUsername} size={26} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selfUsername}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--ft-ink-3)' }}>that's you</span>
              </span>
            </span>
            <ChevronRightIcon />
          </button>
        )}
        {mates.map((m) => (
          <button
            key={m.name}
            className="ft-railaction"
            onClick={() => navigate(`/manager-intel/${encodeURIComponent(m.name)}`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <SleeperAvatar url={m.avatarUrl} name={m.name} size={26} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--ft-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.league}</span>
              </span>
            </span>
            <ChevronRightIcon />
          </button>
        ))}
      </div>
    )}
  </>
);

const IntelScoutCard = ({ navigate, showChips }) => {
  const [q, setQ] = useState('');
  const go = (e) => {
    e.preventDefault();
    const username = q.trim();
    if (username) navigate(`/manager-intel/${encodeURIComponent(username)}`);
  };
  return (
    <div className="ft-deskcard">
      <div>
        <span className="ft-k brand">Manager Intel</span>
        <div className="t">Scout any manager.</div>
        <p className="s">Trade history, tendencies, titles — the full read on any Sleeper username.</p>
      </div>
      <form className="demo" onSubmit={go}>
        <div className="ft-scoutrow">
          <input
            className="ft-scoutin"
            placeholder="Sleeper username"
            aria-label="Sleeper username"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="ft-btn sm" disabled={!q.trim()}>Scout</button>
        </div>
        {showChips ? (
          <div className="ft-pillrow" style={{ marginTop: 12 }}>
            <span className="ft-chip brand">≈2× typical volume</span>
            <span className="ft-chip outline">Hoards picks</span>
          </div>
        ) : null}
      </form>
    </div>
  );
};

const AnalystReviewCard = ({ sub, buttonLabel, onRun }) => (
  <button className="ft-deskcard" onClick={onRun}>
    <div>
      <span className="ft-k brand">Analyst Review</span>
      <div className="t">A real analyst grades it.</div>
      <p className="s">{sub}</p>
    </div>
    <div className="demo">
      <div style={{ display: 'grid', gap: 7, marginBottom: 14 }}>
        {[['Value', 8], ['Timeline fit', 9], ['Capital impact', 7]].map(([label, score]) => (
          <div key={label} style={{ display: 'grid', gridTemplateColumns: '84px 1fr 36px', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>{label}</span>
            <span style={{ height: 8, borderRadius: 980, background: '#FFFFFF', overflow: 'hidden', display: 'block' }}>
              <span style={{ display: 'block', width: `${score * 10}%`, height: '100%', background: 'var(--ft-brand)', borderRadius: 980 }} />
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 650, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{score}/10</span>
          </div>
        ))}
      </div>
      <span className="ft-btn sm">{buttonLabel}</span>
    </div>
  </button>
);

const ToolRows = ({ navigate }) => (
  <div className="ft-section">
    <span className="ft-k">Trade tools</span>
    <div className="ft-toolrows">
      {TRADE_TOOLS.map((tool) => (
        <button key={tool.path} className="ft-toolrow" onClick={() => navigate(tool.path)}>
          <span className="tt">{tool.label}</span>
          <span className="td">{tool.desc}</span>
          <ChevronRightIcon />
        </button>
      ))}
    </div>
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagues, leaguesLoading, drafts, sleeperUser } = useShell();
  const { setRail } = useShellUI();

  const [attention, setAttention] = useState([]);
  const [recentManagers, setRecentManagers] = useState([]);

  // Today's Curve + the tape — shared by the desk panel and the rail.
  const [today, setToday] = useState(null);
  const [tape, setTape] = useState(null);
  const [mates, setMates] = useState([]);
  const claimedRef = useRef(false);

  const loadCurve = useCallback(() => {
    fetchToday()
      .then(setToday)
      // A background refresh (resume/poll) failing shouldn't blank out an
      // already-loaded line — only a true first load falls back to empty.
      .catch(() => setToday((s) => (s && s.line ? s : { line: null })));
    fetchTape()
      .then(setTape)
      .catch(() => {});
  }, []);

  useEffect(() => { loadCurve(); }, [loadCurve, user?.id]);

  // Same fix as CurveToday: phones background the tab/PWA and the desk
  // panel's counts go stale or a fetch hangs — refresh on resume plus a
  // slow poll while Home stays open.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadCurve();
    };
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(onVisible, 45000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(poll);
    };
  }, [loadCurve]);

  // Signed in with leftover guest draws? Claim them into the account once.
  useEffect(() => {
    if (!user || claimedRef.current) return;
    const guest = getGuestToken();
    if (!guest) return;
    claimedRef.current = true;
    claimGuestDraws(guest)
      .then(() => clearGuestToken())
      .catch(() => {})
      .finally(loadCurve);
  }, [user, loadCurve]);

  const handleLocked = useCallback((result) => {
    setToday((s) => (s ? { ...s, ...result } : s));
    fetchTape().then(setTape).catch(() => {});
  }, []);

  // Attention rows — only what the APIs actually return (authed only).
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    Promise.allSettled([
      tradePackagesAPI.list(),
      orphanMarketplaceAPI.getMyApplications(),
      startupMarketplaceAPI.getMyApplications(),
    ]).then(([packagesRes, orphanAppsRes, startupAppsRes]) => {
      if (!active) return;
      const rows = [];
      if (packagesRes.status === 'fulfilled') {
        asArray(packagesRes.value.data, 'packages', 'tradePackages')
          .filter((p) => p.status === 'graded' && p.role !== 'recipient')
          .slice(0, 3)
          .forEach((p) => {
            const who = p.recipientDisplayName || p.recipientUsername;
            rows.push({
              key: `pkg-${p.id || p.uniqueLink}`,
              text: `Your package${who ? ` to ${who}` : ''} was graded${p.avgGrade ? ` — ${p.avgGrade} average` : ''}`,
              detail: 'Trade Packages',
              to: p.uniqueLink ? `/trade-packages/${p.uniqueLink}` : '/trade-packages',
            });
          });
      }
      const appRows = (res, label, basePath) => {
        if (res.status !== 'fulfilled') return;
        asArray(res.value.data, 'applications')
          .filter((a) => a.status && a.status !== 'rejected')
          .slice(0, 3)
          .forEach((a) => {
            const listing =
              a.orphanTeam || a.OrphanTeam || a.startupListing || a.StartupListing || a.listing || {};
            rows.push({
              key: `${label}-${a.id}`,
              text: `${listing.leagueName || listing.teamName || label} — ${a.status}`,
              detail: label,
              to: listing.id ? `${basePath}/${listing.id}` : '/dashboard',
            });
          });
      };
      appRows(orphanAppsRes, 'Orphan application', '/orphan-marketplace');
      appRows(startupAppsRes, 'Startup application', '/startup-marketplace');
      setAttention(rows.slice(0, 5));
    });
    return () => {
      active = false;
    };
  }, [user]);

  // Logged-out rail data — recently scouted managers.
  useEffect(() => {
    if (user) return undefined;
    let active = true;
    managerIntelAPI
      .getRecentManagers(6)
      .then((res) => {
        if (!active) return;
        const list = asArray(res.data, 'managers');
        setRecentManagers(
          list
            .map((m) => ({ username: m.sleeperUsername || m.username, avatarUrl: m.avatarUrl }))
            .filter((m) => m.username)
            .slice(0, 6)
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  // Leaguemates for the rail — owner names from the cached league rosters.
  useEffect(() => {
    if (!user || !leagues.length) {
      setMates([]);
      return undefined;
    }
    let active = true;
    const targets = leagues.slice(0, 2);
    Promise.allSettled(targets.map((l) => sleeperAPI.getLeague(l.leagueId))).then((results) => {
      if (!active) return;
      const seen = new Set();
      const rows = [];
      results.forEach((res, i) => {
        if (res.status !== 'fulfilled') return;
        const rosters = asArray(res.value.data?.rosters);
        rosters.forEach((r) => {
          const name = r.ownerDisplayName;
          if (!name || name === 'Unknown') return;
          if (sleeperUser?.userId && r.sleeperUserId === sleeperUser.userId) return;
          if (user.sleeperUsername && name.toLowerCase() === user.sleeperUsername.toLowerCase()) return;
          if (seen.has(name)) return;
          seen.add(name);
          rows.push({ name, avatarUrl: r.avatarUrl, league: targets[i].name });
        });
      });
      setMates(rows.slice(0, 5));
    });
    return () => {
      active = false;
    };
  }, [user, leagues, sleeperUser]);

  useEffect(() => {
    setRail(
      user ? (
        <AuthedRail
          user={user}
          tape={tape}
          todayLineId={today?.line?.id || null}
          selfUsername={user.sleeperUsername || null}
          selfAvatar={sleeperUser?.avatar}
          mates={mates}
          navigate={navigate}
        />
      ) : (
        <HomeRail
          recentManagers={recentManagers}
          navigate={navigate}
        />
      )
    );
    return () => setRail(null);
  }, [setRail, user, tape, today, mates, sleeperUser, recentManagers, navigate]);

  const resumeCards = useMemo(
    () =>
      Object.entries(drafts)
        .map(([key, draft]) => ({ key, ...draft }))
        .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
        .slice(0, 4),
    [drafts]
  );

  const goSimulate = (league) => {
    if (!league) {
      navigate('/simulate');
      return;
    }
    const params = new URLSearchParams();
    if (league.name) params.set('name', league.name);
    if (sleeperUser?.userId) params.set('userId', sleeperUser.userId);
    if (sleeperUser?.username) params.set('username', sleeperUser.username.toLowerCase());
    navigate(`/simulate/${league.leagueId}?${params.toString()}`);
  };

  const desk = (
    <div className="ft-desk">
      <CurveDeskPanel
        data={today}
        tape={tape}
        streak={tape?.streak || 0}
        user={user}
        onLocked={handleLocked}
        onResync={loadCurve}
      />
      <div className="ft-deskstack">
        <IntelScoutCard navigate={navigate} showChips={!user} />
        <AnalystReviewCard
          sub={
            user
              ? 'Value, timeline fit, capital impact — a real dynasty analyst breaks down your next trade.'
              : 'Get a professional\'s read before you make the deal.'
          }
          buttonLabel="Get it reviewed"
          onRun={() => navigate('/analyst/fantasynerdboi')}
        />
      </div>
    </div>
  );

  if (!user) {
    return (
      <div className="ft-view">
        <div className="ft-homehead">
          <span className="ft-k brand">Fantasy Taco</span>
          <h1 className="ft-hero">Control your dynasty.</h1>
          <p className="ft-sub">
            Play the daily game, get a trade reviewed, scout any manager. Most of it works without
            an account.
          </p>
        </div>

        {desk}

        <ToolRows navigate={navigate} />
      </div>
    );
  }

  return (
    <div className="ft-view">
      <div className="ft-homehead">
        <span className="ft-k brand">Fantasy Taco</span>
        <h1 className="ft-hero">Welcome back.</h1>
        {leagues.length > 0 ? (
          <p className="ft-sub">{leagues.length} league{leagues.length === 1 ? '' : 's'}.</p>
        ) : null}
      </div>

      {desk}

      {resumeCards.length > 0 && (
        <div className="ft-section">
          <span className="ft-k">Pick up where you left off</span>
          <div className="ft-rowcards">
            {resumeCards.map((card) => (
              <button key={card.key} className="ft-resume" onClick={() => navigate(card.resumeUrl || '/home')}>
                <span className="ft-k">{card.featureLabel || 'In progress'}</span>
                <div className="t">{card.label}</div>
                <div className="d">{card.detail || 'Resume'}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ft-section">
        <span className="ft-k">Your leagues</span>
        {leaguesLoading ? (
          <div className="ft-loading">Loading your leagues…</div>
        ) : leagues.length === 0 ? (
          !sleeperUser?.username && !user?.sleeperUsername ? (
            <div style={{ marginTop: 14 }}>
              <LinkSleeperBlock />
            </div>
          ) : (
            <p className="ft-sub" style={{ marginTop: 14 }}>
              No dynasty leagues found for your Sleeper account yet.
            </p>
          )
        ) : (
          <div className="ft-rowcards">
            {leagues.map((league) => (
              <button
                key={league.leagueId}
                className="ft-leaguecard"
                onClick={() => goSimulate(league)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <SleeperAvatar id={league.avatar} name={league.name} size={38} radius="10px" />
                  <div style={{ minWidth: 0 }}>
                    <div className="nm" style={{ marginBottom: 1 }}>{league.name}</div>
                    <div className="tm" style={{ marginBottom: 0 }}>
                      Season {league.season}
                      {league.previousSeasons?.length ? ` · ${league.previousSeasons.length + 1} seasons of history` : ''}
                    </div>
                  </div>
                </div>
                <div className="ft-pillrow">
                  <span className="ft-chip">{leagueMeta(league)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <ToolRows navigate={navigate} />

      {attention.length > 0 && (
        <div className="ft-section">
          <span className="ft-k">Needs your attention</span>
          <div style={{ marginTop: 6 }}>
            {attention.map((row) => (
              <button key={row.key} className="ft-notif" onClick={() => navigate(row.to)}>
                <div className="ic">
                  <ChevronRightIcon />
                </div>
                <div>
                  <div className="nt">{row.text}</div>
                  <div className="nd">{row.detail}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
